/**
 * @file Shared board-table interaction wiring.
 *
 * Owns group collapse, row/action clicks, column reorder, and column resizing.
 */

import { state } from "../../app/state/state.js";

const ACTION_COLUMN_WIDTH_REM = 3;
const DEFAULT_COLUMN_WIDTH_WEIGHT = 120;
const MIN_COLUMN_WIDTH_WEIGHT = 72;
const TABLE_DESKTOP_MIN_WIDTH = 640;

function isDesktopTableInteraction() {
  return (window.innerWidth || document.documentElement.clientWidth || 0) >= TABLE_DESKTOP_MIN_WIDTH;
}

function getColumnWidthWeight(columnKey, columnWidths = {}) {
  const savedWidth = Number(columnWidths[columnKey]);
  if (!Number.isFinite(savedWidth) || savedWidth <= 0) {
    return DEFAULT_COLUMN_WIDTH_WEIGHT;
  }
  return Math.max(savedWidth, MIN_COLUMN_WIDTH_WEIGHT);
}

function getActionColumnCount(table) {
  return table?.querySelectorAll("thead th:not([data-key])").length || 0;
}

function getColumnWidthStyle({
  columnKey,
  columnWidths,
  visibleColumns,
}) {
  const totalWeight = visibleColumns.reduce(
    (total, key) => total + getColumnWidthWeight(key, columnWidths),
    0,
  );
  const columnWeight = getColumnWidthWeight(columnKey, columnWidths);

  if (totalWeight <= 0) {return "";}

  return `${(columnWeight / totalWeight) * 100}%`;
}

function applyColumnWidthWeightsAcrossGroups(boardContainer, visibleColumns, columnWidths) {
  if (!boardContainer || !Array.isArray(visibleColumns) || visibleColumns.length === 0) {
    return;
  }

  boardContainer.querySelectorAll("table").forEach((table) => {
    visibleColumns.forEach((columnKey) => {
      table.querySelectorAll(`th[data-key="${columnKey}"]`).forEach((headerCell) => {
        headerCell.style.width = getColumnWidthStyle({
          columnKey,
          columnWidths,
          visibleColumns,
        });
      });
    });
  });
}

/**
 * Adds behavior listeners to a rendered board table.
 * @param {HTMLElement} boardContainer - Table board container.
 * @param {object} options - Interaction callbacks and metadata.
 * @param {string} options.viewType - Singular view type.
 * @param {string} [options.viewKey] - View settings key override.
 * @param {Function} options.saveViewSettings - View settings persistence callback.
 * @param {Function} options.getTaskItems - Callback returning task table items.
 * @param {Function} options.renderTableForView - View rerender callback.
 * @param {Function} options.openTaskForm - Quick-add task callback.
 * @param {Function} options.openDetails - Row navigation callback.
 * @param {Function} options.quickCloseTask - Task quick-close callback.
 * @param {Function} options.loadMoreClosedItems - Closed pagination callback.
 * @returns {void}
 */
export function addBoardTableInteractionListeners(boardContainer, {
  viewType,
  viewKey = `${viewType}s`,
  saveViewSettings,
  getTaskItems,
  renderTableForView,
  openTaskForm,
  openDetails,
  quickCloseTask,
  loadMoreClosedItems,
}) {
  if (!boardContainer) {
    console.error("addTableInteractionListeners: boardContainer is invalid.");
    return;
  }
  const settingsKey = viewKey;
  let draggedColumnKey = null;

  const resizeState = {
    thElm: null,
    startX: 0,
    wrapperWidth: 0,
    totalWeight: 0,
    startWeight: 0,
    neighborStartWeight: 0,
    resizeColumnKey: null,
    neighborColumnKey: null,
    workingColumnWidths: null,
    boundMouseMove: null,
    boundMouseUp: null,
  };

  const handleClick = (event) => {
    const groupToggleButton = event.target.closest("[data-group-toggle]");
    if (groupToggleButton) {
      event.stopPropagation();
      const groupId = groupToggleButton.dataset.groupToggle;
      const groupContainer = groupToggleButton.closest("[data-group-id]");
      const groupContent = groupContainer?.querySelector(`[data-group-content="${groupId}"]`);
      const icon = groupToggleButton.querySelector(".material-symbols-outlined");
      const isCollapsed = groupContent?.classList.toggle("hidden");
      groupToggleButton.setAttribute("aria-expanded", isCollapsed ? "false" : "true");
      if (icon) {
        icon.textContent = isCollapsed ? "chevron_right" : "expand_more";
      }
      return;
    }

    if (event.target.closest(".task-quick-close-btn")) {
      event.stopPropagation();
      const button = event.target.closest(".task-quick-close-btn");
      if (!button?.dataset.taskId) {
        return;
      }
      button.disabled = true;
      Promise.resolve(quickCloseTask(button.dataset.taskId))
        .catch((error) => {
          console.error("Failed to quick close task:", error);
        })
        .finally(() => {
          button.disabled = false;
        });
    } else if (event.target.closest(".quick-add-task-btn")) {
      event.stopPropagation();
      const button = event.target.closest(".quick-add-task-btn");
      const parentId = button?.dataset[`${viewType}Id`];
      if (!parentId) {
        return;
      }
      openTaskForm({ parentId, owner: state.currentUser?.uid });
    } else if (event.target.closest(".load-more-closed-btn")) {
      event.stopPropagation();
      const button = event.target.closest(".load-more-closed-btn");
      const collectionType = button?.dataset.collectionType;
      if (!collectionType) {
        return;
      }
      button.disabled = true;
      Promise.resolve(loadMoreClosedItems(collectionType))
        .finally(() => {
          button.disabled = false;
        });
    } else if (event.target.closest("button")) {
      return;
    } else if (event.target.closest("tr[data-id]")) {
      const row = event.target.closest("tr[data-id]");
      openDetails(row.dataset.id);
    }
  };

  const handleDragStart = (event) => {
    const header = event.target.closest(".sortable");
    if (!header || event.target.closest(".table-resizer, button") || !isDesktopTableInteraction()) {
      event.preventDefault();
      return;
    }

    if (header.dataset.key) {
      draggedColumnKey = header.dataset.key;
      header.classList.add("dragging-header");
      try {
        event.dataTransfer.setData("text/plain", draggedColumnKey);
      } catch (error) {
        console.warn("Could not set drag data", error);
      }
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
      }
    }
  };

  const handleDragEnd = (event) => {
    const header = event.target.closest(".sortable");
    if (header) {
      header.classList.remove("dragging-header");
      boardContainer.querySelectorAll(".drag-over-header")
        .forEach((element) => element.classList.remove("drag-over-header"));
      draggedColumnKey = null;
    }
  };

  const handleDragOver = (event) => {
    const targetHeader = event.target.closest(".sortable");
    if (targetHeader && draggedColumnKey && targetHeader.dataset.key !== draggedColumnKey) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      boardContainer.querySelectorAll(".drag-over-header").forEach((element) => {
        if (element !== targetHeader) {
          element.classList.remove("drag-over-header");
        }
      });
      targetHeader.classList.add("drag-over-header");
    } else if (targetHeader && draggedColumnKey && targetHeader.dataset.key === draggedColumnKey) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "none";
      }
      targetHeader.classList.remove("drag-over-header");
    }
  };

  const handleDragLeave = (event) => {
    event.target.closest(".sortable")?.classList.remove("drag-over-header");
  };

  const handleDrop = (event) => {
    const targetHeader = event.target.closest(".sortable");
    if (targetHeader && draggedColumnKey) {
      event.preventDefault();
      targetHeader.classList.remove("drag-over-header");
      const targetKey = targetHeader.dataset.key;
      const viewSettings = state.viewSettings?.[settingsKey];

      if (viewSettings?.visibleColumns && targetKey !== draggedColumnKey) {
        const cols = viewSettings.visibleColumns;
        const draggedIndex = cols.indexOf(draggedColumnKey);
        const targetIndex = cols.indexOf(targetKey);
        if (draggedIndex > -1 && targetIndex > -1) {
          const [draggedItem] = cols.splice(draggedIndex, 1);
          cols.splice(targetIndex, 0, draggedItem);
          saveViewSettings();
          renderTableForView(viewType, boardContainer, getTaskItems());
        } else {
          console.warn("Drop failed: Invalid indices.");
        }
      } else {
        console.warn("Drop failed: Invalid state/keys (maybe dropped on self?).");
      }
    }
  };

  const onResizeMouseMoveBound = (event) => {
    if (
      !resizeState.thElm ||
      !resizeState.resizeColumnKey ||
      !resizeState.neighborColumnKey ||
      !resizeState.workingColumnWidths
    ) {
      return;
    }
    event.preventDefault();
    const viewSettings = state.viewSettings?.[settingsKey];
    const visibleColumns = viewSettings?.visibleColumns || [];
    if (!Array.isArray(visibleColumns) || visibleColumns.length === 0 || resizeState.wrapperWidth <= 0) {
      return;
    }

    const pairTotalWeight = resizeState.startWeight + resizeState.neighborStartWeight;
    const pairMinimumWeight = Math.min(MIN_COLUMN_WIDTH_WEIGHT, pairTotalWeight / 2);
    const deltaWeight = ((event.clientX - resizeState.startX) / resizeState.wrapperWidth) *
      resizeState.totalWeight;
    const nextWeight = Math.max(
      pairMinimumWeight,
      Math.min(
        resizeState.startWeight + deltaWeight,
        pairTotalWeight - pairMinimumWeight,
      ),
    );
    const nextNeighborWeight = pairTotalWeight - nextWeight;

    resizeState.workingColumnWidths = {
      ...resizeState.workingColumnWidths,
      [resizeState.resizeColumnKey]: Number(nextWeight.toFixed(2)),
      [resizeState.neighborColumnKey]: Number(nextNeighborWeight.toFixed(2)),
    };

    applyColumnWidthWeightsAcrossGroups(
      boardContainer,
      visibleColumns,
      resizeState.workingColumnWidths,
    );
  };

  const onResizeMouseUpBound = () => {
    if (!resizeState.thElm || !resizeState.resizeColumnKey || !resizeState.workingColumnWidths) {
      return;
    }

    const viewSettings = state.viewSettings?.[settingsKey];

    if (viewSettings) {
      viewSettings.columnWidths = {
        ...(viewSettings.columnWidths || {}),
        ...resizeState.workingColumnWidths,
      };
      saveViewSettings();
    }

    document.removeEventListener("mousemove", resizeState.boundMouseMove);
    document.removeEventListener("mouseup", resizeState.boundMouseUp);
    resizeState.thElm = null;
    resizeState.resizeColumnKey = null;
    resizeState.neighborColumnKey = null;
    resizeState.workingColumnWidths = null;
    resizeState.boundMouseMove = null;
    resizeState.boundMouseUp = null;
  };

  const handleMouseDownResize = (event) => {
    if (event.target.matches(".table-resizer")) {
      event.stopPropagation();
      event.preventDefault();
      resizeState.thElm = event.target.closest("th");
      resizeState.resizeColumnKey = resizeState.thElm?.dataset.key;
      resizeState.neighborColumnKey = event.target.dataset.neighborKey || "";
      const viewSettings = state.viewSettings?.[settingsKey];
      const visibleColumns = viewSettings?.visibleColumns || [];
      if (
        !resizeState.thElm ||
        !resizeState.resizeColumnKey ||
        !resizeState.neighborColumnKey ||
        !Array.isArray(visibleColumns) ||
        !visibleColumns.includes(resizeState.resizeColumnKey) ||
        !visibleColumns.includes(resizeState.neighborColumnKey)
      ) {
        resizeState.thElm = null;
        resizeState.resizeColumnKey = null;
        resizeState.neighborColumnKey = null;
        return;
      }

      const tableWrapper = resizeState.thElm.closest("[data-group-content]");
      const actionColumnCount = getActionColumnCount(resizeState.thElm.closest("table"));
      const reservedWidth = actionColumnCount * ACTION_COLUMN_WIDTH_REM * 16;
      resizeState.startX = event.clientX;
      resizeState.wrapperWidth = Math.max(
        1,
        (tableWrapper?.clientWidth || resizeState.thElm.closest("table")?.clientWidth || 1) - reservedWidth,
      );
      resizeState.workingColumnWidths = {...(viewSettings.columnWidths || {})};
      resizeState.startWeight = getColumnWidthWeight(
        resizeState.resizeColumnKey,
        resizeState.workingColumnWidths,
      );
      resizeState.neighborStartWeight = getColumnWidthWeight(
        resizeState.neighborColumnKey,
        resizeState.workingColumnWidths,
      );
      resizeState.totalWeight = visibleColumns.reduce(
        (total, columnKey) => total + getColumnWidthWeight(columnKey, resizeState.workingColumnWidths),
        0,
      );
      resizeState.boundMouseMove = onResizeMouseMoveBound;
      resizeState.boundMouseUp = onResizeMouseUpBound;
      document.addEventListener("mousemove", resizeState.boundMouseMove);
      document.addEventListener("mouseup", resizeState.boundMouseUp);
    }
  };

  if (boardContainer._tableListeners) {
    boardContainer.removeEventListener("click", boardContainer._tableListeners.click);
    boardContainer.removeEventListener("dragstart", boardContainer._tableListeners.dragstart);
    boardContainer.removeEventListener("dragend", boardContainer._tableListeners.dragend);
    boardContainer.removeEventListener("dragover", boardContainer._tableListeners.dragover);
    boardContainer.removeEventListener("dragleave", boardContainer._tableListeners.dragleave);
    boardContainer.removeEventListener("drop", boardContainer._tableListeners.drop);
    boardContainer.removeEventListener("mousedown", boardContainer._tableListeners.mousedown);
    if (resizeState.boundMouseMove) {
      document.removeEventListener("mousemove", resizeState.boundMouseMove);
    }
    if (resizeState.boundMouseUp) {
      document.removeEventListener("mouseup", resizeState.boundMouseUp);
    }
  }

  boardContainer.addEventListener("click", handleClick);
  boardContainer.addEventListener("dragstart", handleDragStart);
  boardContainer.addEventListener("dragend", handleDragEnd);
  boardContainer.addEventListener("dragover", handleDragOver);
  boardContainer.addEventListener("dragleave", handleDragLeave);
  boardContainer.addEventListener("drop", handleDrop);
  boardContainer.addEventListener("mousedown", handleMouseDownResize);

  boardContainer._tableListeners = {
    click: handleClick,
    dragstart: handleDragStart,
    dragend: handleDragEnd,
    dragover: handleDragOver,
    dragleave: handleDragLeave,
    drop: handleDrop,
    mousedown: handleMouseDownResize,
  };
}
